import { type DocumentNode, useApolloClient } from '@apollo/client';
import { Upload, type UploadProps, message } from 'antd';
import { flow } from 'lodash';
import { filter, isEmpty, isNil, map } from 'lodash/fp';
import { useCallback, useMemo, useReducer, useState } from 'react';
import { type TFunction, useTranslation } from 'react-i18next';
import getApolloErrors from '../../../server/utils/getApolloErrors';
import {
    type ImportMakeMutation,
    type ImportMakeMutationVariables,
    ImportMakeDocument,
} from '../../api/mutations/importMake';
import {
    type ImportModelMutation,
    type ImportModelMutationVariables,
    ImportModelDocument,
} from '../../api/mutations/importModel';
import {
    type ImportSubModelMutation,
    type ImportSubModelMutationVariables,
    ImportSubModelDocument,
} from '../../api/mutations/importSubModel';
import {
    type ImportVariantMutation,
    type ImportVariantMutationVariables,
    ImportVariantDocument,
} from '../../api/mutations/importVariant';
import { ListLocalMakesDocument } from '../../api/queries/listLocalMakes';
import { ListLocalModelsDocument } from '../../api/queries/listLocalModels';
import { ListLocalVariantsDocument } from '../../api/queries/listLocalVariants';
import { CompanyTheme } from '../../api/types';
import { useThemeComponents } from '../../themes';
import { DownloadType, exportVehicles } from '../../utilities/export';
import { allowedExtensions, validateExtensions } from '../../utilities/extensions';
import { useAccountContext } from '../contexts/AccountContextManager';
import { useCompany } from '../contexts/CompanyContextManager';
import {
    ErrorContent,
    InformationContent,
    UpdateDuplicateContent,
    defaultState,
    reducer,
    type DuplicateIds,
} from '../importExport';
import CancelButton from '../modal/CancelButton';
import OkButton from '../modal/OkButton';
import { UploadContainer } from './ui';
import UploadContent from './UploadContent';

type Mutation = ImportMakeMutation | ImportModelMutation | ImportSubModelMutation | ImportVariantMutation;

type Variables =
    | ImportMakeMutationVariables
    | ImportModelMutationVariables
    | ImportSubModelMutationVariables
    | ImportVariantMutationVariables;

type ExcelUploadModalProps = {
    open: boolean;
    onClose: () => void;
    title: string;
    vehicleType: string;
    kind: DownloadType;
    t: TFunction;
    mutation: DocumentNode;
    document: DocumentNode;
    moduleId: string;
};

const getVehicleData = (kind: DownloadType, t: TFunction) => {
    switch (kind) {
        case DownloadType.Makes:
            return {
                title: t('importExportExcel:import.make.title'),
                vehicleType: t('importExportExcel:import.make.type'),
                mutation: ImportMakeDocument,
                document: ListLocalMakesDocument,
            };

        case DownloadType.Models:
            return {
                title: t('importExportExcel:import.model.title'),
                vehicleType: t('importExportExcel:import.model.type'),
                mutation: ImportModelDocument,
                document: ListLocalModelsDocument,
            };

        case DownloadType.SubModels:
            return {
                title: t('importExportExcel:import.subModel.title'),
                vehicleType: t('importExportExcel:import.subModel.type'),
                mutation: ImportSubModelDocument,
                document: ListLocalModelsDocument,
            };

        case DownloadType.Variants:
            return {
                title: t('importExportExcel:import.variant.title'),
                vehicleType: t('importExportExcel:import.variant.type'),
                mutation: ImportVariantDocument,
                document: ListLocalVariantsDocument,
            };

        default:
            throw new Error('Invalid Vehicle');
    }
};

const ExcelUploadModal = ({
    open,
    onClose,
    title,
    vehicleType,
    kind,
    mutation,
    document,
    moduleId,
    t,
}: ExcelUploadModalProps) => {
    const company = useCompany(true);
    const { token } = useAccountContext();
    const { id: companyId = null, displayName, countryCode } = company || {};
    const apolloClient = useApolloClient();
    const { theme, Modal } = useThemeComponents();

    const [state, dispatch] = useReducer(reducer, defaultState);
    const { file, errors, updateCount, duplicates } = state;

    const actions = useMemo(
        () => ({
            setUpdateCount: (updateCount: number | null) => dispatch({ type: 'setUpdateCount', updateCount }),
            setDuplicates: (duplicates: DuplicateIds[] | null) => dispatch({ type: 'setDuplicates', duplicates }),
            setErrors: (errors: string | null) => dispatch({ type: 'setErrors', errors }),
            setFile: (file: File | null) => dispatch({ type: 'setFile', file }),
            setDefault: () => dispatch({ type: 'setDefault' }),
        }),
        [dispatch]
    );

    const onCancelHandler = useCallback(() => {
        onClose();
        actions.setDefault();
    }, [onClose, actions]);

    const onUpload = useCallback(
        async inputFile => {
            try {
                actions.setFile(inputFile);
                const { data } = await apolloClient.mutate<Mutation, Variables>({
                    mutation,
                    variables: {
                        companyId,
                        upload: inputFile,
                        duplicateIdsToBeUpdated: [],
                        isUpdate: false,
                        moduleId,
                    },
                    refetchQueries: [document],
                });
                const { updateCount: count, duplicateIds } = data.response;

                if (duplicateIds.length > 0) {
                    const duplicated = duplicateIds.length > 1 ? ['Select all', ...duplicateIds] : duplicateIds;
                    actions.setDuplicates(duplicated.map(id => ({ label: id, key: id, value: false })));
                } else {
                    actions.setUpdateCount(count);
                }
            } catch (error) {
                const apolloErrors = getApolloErrors(error);

                if (apolloErrors?.$root) {
                    actions.setErrors(apolloErrors?.$root);
                }
            }
        },
        [apolloClient, companyId, mutation, document, actions, moduleId]
    );

    const onDuplicateClick = useCallback(
        item => {
            if (item.key === 'Select all') {
                const updatedDuplicates = duplicates.map(duplicate => ({ ...duplicate, value: !item.value }));

                actions.setDuplicates(updatedDuplicates);
            } else {
                const updatedDuplicates = duplicates.map(duplicate => {
                    if (duplicate.key === item.key) {
                        return { ...duplicate, value: !item.value };
                    }

                    return duplicate;
                });

                actions.setDuplicates(updatedDuplicates);
            }
        },
        [duplicates, actions]
    );

    const onOverrideClick = useCallback(async () => {
        try {
            const duplicateIdsToBeUpdated = flow([
                filter((x: DuplicateIds) => x.value && x.key !== 'Select all'),
                map((x: DuplicateIds) => x.key),
            ])(duplicates);

            const { data } = await apolloClient.mutate<Mutation, Variables>({
                mutation,
                variables: {
                    companyId,
                    upload: file,
                    duplicateIdsToBeUpdated,
                    isUpdate: true,
                    moduleId,
                },
                refetchQueries: [document],
            });

            const { updateCount: count } = data.response;

            actions.setUpdateCount(count);
        } catch (error) {
            const apolloErrors = getApolloErrors(error);

            if (apolloErrors?.$root) {
                actions.setErrors(apolloErrors?.$root);
            }
        }
    }, [apolloClient, companyId, file, duplicates, mutation, document, actions, moduleId]);

    const handleOnChange = useCallback(
        ({ file }) => {
            if (file instanceof File) {
                onUpload(file);
            }
        },
        [onUpload]
    );
    const beforeUpload = useCallback<NonNullable<UploadProps['beforeUpload']>>(
        file => {
            const reader = new FileReader();
            const promise = new Promise<boolean | string>((resolve, reject) => {
                reader.onload = async event => {
                    const content = event.target.result as string;
                    const validationResult = await validateExtensions(content, file.name);
                    if (validationResult.valid) {
                        return resolve(false);
                    }
                    message.error(t('common:upload.messages.fileError'));

                    return resolve(Upload.LIST_IGNORE);
                };

                reader.onerror = event => resolve(Upload.LIST_IGNORE);
            });

            reader.readAsBinaryString(file);

            return Promise.resolve(promise);
        },
        [t]
    );
    const props: UploadProps = useMemo(
        () => ({
            accept: allowedExtensions.excel.join(','),
            showUploadList: false,
            onChange: handleOnChange,
            beforeUpload,
        }),
        [beforeUpload, handleOnChange]
    );

    const content = useMemo(() => {
        const onDownloadClick = () => {
            exportVehicles(displayName, countryCode, kind, true, moduleId, token);
        };

        if (!isNil(errors)) {
            return <ErrorContent errors={errors} />;
        }

        if (!isNil(updateCount)) {
            return <InformationContent type={vehicleType} updateCount={updateCount} />;
        }

        if (!isEmpty(duplicates)) {
            return <UpdateDuplicateContent duplicates={duplicates} onDuplicateClick={onDuplicateClick} />;
        }

        return <UploadContent companyName={displayName} onDownloadClick={onDownloadClick} />;
    }, [
        errors,
        updateCount,
        duplicates,
        displayName,
        countryCode,
        kind,
        moduleId,
        token,
        vehicleType,
        onDuplicateClick,
    ]);

    const footer = useMemo(() => {
        const cancelButton = (
            <CancelButton key="cancel" onClick={onCancelHandler}>
                {t('importExportExcel:actions.cancel')}
            </CancelButton>
        );

        const okButton = !isEmpty(duplicates) ? (
            <OkButton key="submit" onClick={onOverrideClick}>
                {t('importExportExcel:actions.confirmOverride')}
            </OkButton>
        ) : (
            <UploadContainer {...props}>
                <OkButton key="submit">{t('importExportExcel:actions.uploadFile')}</OkButton>
            </UploadContainer>
        );

        if (theme === CompanyTheme.PorscheV3 || theme === CompanyTheme.Porsche) {
            return [okButton, cancelButton];
        }

        return [cancelButton, okButton];
    }, [duplicates, onCancelHandler, onOverrideClick, props, theme, t]);

    return (
        <Modal footer={footer} onCancel={onCancelHandler} open={open} title={title} width={550}>
            {content}
        </Modal>
    );
};

export default ExcelUploadModal;

export const useExcelUploadModal = (kind: DownloadType) => {
    const { t } = useTranslation('importExportExcel');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [moduleId, setModuleId] = useState('');

    const actions = useMemo(() => {
        const close = () => setIsModalOpen(false);

        return {
            open: (moduleId: string) => {
                setIsModalOpen(true);
                setModuleId(moduleId);
            },
            close,
            ...getVehicleData(kind, t),
        };
    }, [setIsModalOpen, kind, t]);

    return {
        ...actions,
        render: () => (
            <ExcelUploadModal
                document={actions.document}
                kind={kind}
                moduleId={moduleId}
                mutation={actions.mutation}
                onClose={actions.close}
                open={isModalOpen}
                t={t}
                title={actions.title}
                vehicleType={actions.vehicleType}
            />
        ),
    };
};
